'use client'

import type { ComponentProps, FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ActionMeta, MultiValue } from 'react-select'

import { AsyncMultipleSearchWithSelect } from '~/core/ui/AsyncMultipleSearchWithSelect'
import type { IPromiseSearchOption, ISelectOption } from '~/core/ui/Select'

// Define the complete data structure interface
interface DataState {
  isFetchedAPI: boolean
  collection: ISelectOption[]
  parentChildsMap: { [key: string]: ISelectOption[] }
  parentMap: { [key: string]: ISelectOption | undefined }
  depMap: { [key: string]: ISelectOption }
  isSearch: boolean
}

type AsyncMultipleSearchWithSelectOptionGroupProps = ComponentProps<
  typeof AsyncMultipleSearchWithSelect
> & {
  selectAllChange?: (value: boolean) => void
  showSelectOptionAll: boolean
  selectAllValue?: string | number
  selectAllLabel?: string
  alwaysSelectedValues?: ISelectOption[]
  /**
   * false: không đồng bộ cha-con, chọn độc lập, hiển thị đúng mọi option đang chọn.
   * true (default): giữ logic đồng bộ cha-con và hiển thị chip cha (nếu toàn bộ con được chọn).
   */
  includeSubordinates?: boolean
  fieldToCheckVisible?: string
}

const AsyncMultipleSearchWithSelectOptionGroup: FC<
  AsyncMultipleSearchWithSelectOptionGroupProps
> = ({ includeSubordinates = true, ...props }) => {
  const { t } = useTranslation()

  // Initialize with complete data structure
  const [data, setData] = useState<DataState>({
    isFetchedAPI: false,
    collection: [],
    parentChildsMap: {},
    parentMap: {},
    depMap: {},
    isSearch: false
  })

  const dataRef = useRef(data)
  dataRef.current = data

  const isSelectAllModeRef = useRef(false)

  const promiseOptions = useCallback(
    (params: IPromiseSearchOption) => {
      if (!props.promiseOptions) {
        return Promise.resolve({
          collection: [] as ISelectOption[],
          metadata: { totalCount: 0 }
        })
      }

      const result = props.promiseOptions(params)
      const promise = result instanceof Promise ? result : Promise.resolve(result)

      return promise.then((result) => {
        const collection =
          (params.page ?? 1) > 1
            ? [...dataRef.current.collection, ...result.collection]
            : result.collection

        const parentChildsMap = collection.reduce(
          (acc, item) =>
            item.parentId
              ? {
                  ...acc,
                  [item.parentId]: [
                    ...(acc[item.parentId] || []).filter((fitem) => fitem.value !== item.value),
                    item
                  ]
                }
              : acc,
          dataRef.current.parentChildsMap || {}
        )

        const parentMap = Object.keys(parentChildsMap).reduce(
          (acc, key) => ({
            ...acc,
            [key]: collection.find((item) => item.value === key)
          }),
          dataRef.current.parentMap || {}
        )

        const depMap = collection.reduce(
          (acc, item) => ({ ...acc, [item.value]: item }),
          {} as { [key: string]: ISelectOption }
        )

        setData({
          isFetchedAPI: true,
          parentMap,
          parentChildsMap,
          depMap,
          collection,
          isSearch: !!params.search
        })

        return {
          ...result,
          collection:
            (params.page ?? 1) > 1 || params.search
              ? result.collection
              : result.collection.length > 0
                ? props.showSelectOptionAll
                  ? [
                      {
                        value: props.selectAllValue,
                        isHideAvatar: true,
                        supportingObj: {
                          name: props?.selectAllLabel
                            ? props?.selectAllLabel
                            : `${t('settings:departments:allDepartments')}`
                        }
                      } as ISelectOption,
                      ...result.collection
                    ]
                  : [...result.collection]
                : []
        } as { metadata?: { totalCount: number }; collection: ISelectOption[] }
      })
    },
    [props.promiseOptions, props.showSelectOptionAll, t]
  ) as NonNullable<AsyncMultipleSearchWithSelectOptionGroupProps['promiseOptions']>

  const onChange = useCallback(
    (value: MultiValue<ISelectOption>, actionMeta: ActionMeta<ISelectOption>) => {
      if (!props.onChange) return

      if (!includeSubordinates) {
        if (actionMeta.action === 'select-option') {
          if (actionMeta.option?.value === props.selectAllValue) {
            isSelectAllModeRef.current = true
            return props.onChange([...data.collection], actionMeta)
          }
          return props.onChange(value, actionMeta)
        }

        if (actionMeta.action === 'deselect-option') {
          if (actionMeta.option?.value === props.selectAllValue) {
            isSelectAllModeRef.current = false
            return props.onChange([], actionMeta)
          }
          return props.onChange(
            value.filter((item) => item.value !== props.selectAllValue),
            actionMeta
          )
        }

        if (actionMeta.action === 'remove-value') {
          if (actionMeta.removedValue?.value === props.selectAllValue) {
            isSelectAllModeRef.current = false
            return props.onChange([], actionMeta)
          }
          return props.onChange(
            value.filter((item) => item.value !== props.selectAllValue),
            actionMeta
          )
        }

        return props.onChange(value, actionMeta)
      }

      const childIds = data.parentChildsMap?.[actionMeta?.option?.value || ''] || []

      if (actionMeta.action === 'select-option') {
        if (actionMeta.option?.value === props.selectAllValue) {
          isSelectAllModeRef.current = true
          return props.onChange([...data.collection], actionMeta)
        }

        if (actionMeta.option?.parentId) {
          const totalChildSelect = value.filter(
            (item) => data.depMap[item.value]?.parentId === actionMeta.option?.parentId
          ).length
          const totalChilds = data.parentChildsMap?.[actionMeta.option?.parentId] || []

          if (totalChildSelect > 0 && totalChildSelect === totalChilds.length) {
            const parentOption = data.parentMap[actionMeta.option?.parentId]
            if (parentOption) {
              return props.onChange([...value, parentOption], actionMeta)
            }
          }
        }

        return props.onChange(
          [
            ...value.filter((item) => !childIds.map((child) => child.value).includes(item.value)),
            ...childIds
          ],
          actionMeta
        )
      }

      if (actionMeta.action === 'deselect-option') {
        const deselectOption = actionMeta.option
        if (deselectOption?.value === props.selectAllValue) {
          isSelectAllModeRef.current = false
          return props.onChange([], actionMeta)
        }
        if (deselectOption?.parentId) {
          return props.onChange(
            value.filter(
              (item) =>
                item.value !== props.selectAllValue && item.value !== deselectOption?.parentId
            ),
            actionMeta
          )
        }
        return props.onChange(
          value.filter(
            (item) =>
              item.value !== props.selectAllValue &&
              !childIds.map((child) => child.value).includes(item.value)
          ),
          actionMeta
        )
      }

      if (actionMeta.action === 'remove-value') {
        if (actionMeta.removedValue?.value === props.selectAllValue) {
          isSelectAllModeRef.current = false

          return props.onChange([], actionMeta)
        }
        const removedValuechildIds =
          data?.parentChildsMap?.[actionMeta?.removedValue?.value || ''] || []

        return props.onChange(
          value.filter(
            (item) =>
              item.value !== props.selectAllValue &&
              !removedValuechildIds.map((child) => child.value).includes(item.value)
          ),
          actionMeta
        )
      }

      return props.onChange(value, actionMeta)
    },
    [data, data.collection, includeSubordinates, props.onChange, props.selectAllValue]
  ) as NonNullable<AsyncMultipleSearchWithSelectOptionGroupProps['onChange']>

  const shortTerningValue = useCallback(
    (valuesVariable: ISelectOption[] | undefined): ISelectOption[] => {
      const values = valuesVariable || []

      const cpMap = values.reduce(
        (acc, item) => {
          return {
            ...acc,
            ...(item?.subordinates || []).reduce(
              (accs, subitem) => ({
                ...accs,
                [subitem.id || '']: item
              }),
              {} as { [key: string]: ISelectOption }
            )
          }
        },
        {} as { [key: string]: ISelectOption }
      )

      const selectedPCMap = values.reduce(
        (acc, item) => {
          const parent = cpMap[item.value]
          return parent ? { ...acc, [parent.value]: [...(acc[parent.value] || []), item] } : acc
        },
        {} as { [key: string]: ISelectOption[] }
      )

      return values.map((item) => {
        const parent = cpMap[item.value]
        const currentChilds = data.parentChildsMap?.[item?.value] || []
        const allSelectedChilds = selectedPCMap[item.value] || []
        const allParentsChild = data.parentChildsMap?.[parent?.value || ''] || []
        const allSelectedParentsChild = selectedPCMap[parent?.value || ''] || []

        const isShowAll =
          (props?.value as ISelectOption[])?.length === data?.collection?.length && !data.isSearch

        if (!includeSubordinates) {
          // Hiển thị đúng những gì user đang chọn.
          return (item.subordinates || []).length > 0
            ? {
                ...item,
                parentId: parent?.value,
                chipHidden: isShowAll
              }
            : parent
              ? {
                  ...item,
                  parentId: parent?.value,
                  chipHidden: isShowAll
                }
              : {
                  ...item,
                  chipHidden: isShowAll
                }
        }

        return (item.subordinates || []).length > 0
          ? {
              ...item,
              parentId: parent?.value,
              chipHidden: isShowAll || currentChilds.length > (allSelectedChilds?.length || 0)
            }
          : parent
            ? {
                ...item,
                parentId: parent?.value,
                chipHidden: isShowAll || allParentsChild.length === allSelectedParentsChild.length
              }
            : {
                ...item,
                chipHidden: isShowAll
              }
      })
    },
    [data, props.value, includeSubordinates]
  )

  useEffect(() => {
    const propValue = props.value as ISelectOption[] | undefined
    const selectAllVal = String((props?.selectAllValue as string) || '0')

    const hasSelectAll = propValue?.some((item) => String(item.value) === selectAllVal) || false

    const isFullSelected =
      data?.isFetchedAPI && (propValue?.length || 0) === (data?.collection?.length || 0)

    const isShowAll = !data.isSearch && (hasSelectAll || isFullSelected)

    if (props.selectAllChange) {
      props.selectAllChange(isShowAll)
    }
  }, [
    props.value,
    data?.isFetchedAPI,
    data?.collection?.length,
    data.isSearch,
    props.selectAllChange,
    props.onChange
  ])
  // useEffect(() => {
  //   const propValue = props.value as ISelectOption[] | undefined

  //   const isSelectAllOnly =
  //     propValue?.length === 1 &&
  //     String(propValue?.[0]?.value) === String((props?.selectAllValue as string) || '0')

  //   // 👇 KEY: expand to full list AFTER options loaded
  //   if (isSelectAllOnly && data.collection.length > 0) {
  //     props.onChange?.(data.collection, {
  //       action: 'select-option'
  //     } as any)
  //   }
  // }, [props.value, data.collection])

  useEffect(() => {
    const propValue = props.value as ISelectOption[] | undefined

    const isSelectAllOnly =
      propValue?.length === 1 &&
      String(propValue?.[0]?.value) === String((props?.selectAllValue as string) || '0')

    if (isSelectAllOnly && data.collection.length > 0) {
      isSelectAllModeRef.current = true

      props.onChange?.(data.collection, {
        action: 'select-option'
      } as any)
    }
  }, [props.value, data.collection])

  const propValue = props.value as ISelectOption[] | undefined
  const isShowAll =
    data?.collection?.length > 0 && propValue?.length === data?.collection?.length && !data.isSearch

  return (
    <AsyncMultipleSearchWithSelect
      {...props}
      value={
        isShowAll
          ? props.showSelectOptionAll
            ? [
                ...shortTerningValue(propValue),
                {
                  value: (props?.selectAllValue as string) || '0',
                  supportingObj: {
                    name: props?.selectAllLabel
                      ? props?.selectAllLabel
                      : `${t('settings:departments:allDepartments')}`
                  },
                  chipHidden: data.collection.length > (propValue?.length || 0)
                }
              ]
            : [...(propValue || [])]
          : shortTerningValue(propValue)
      }
      promiseOptions={promiseOptions}
      onChange={onChange}
      classNameOverride={{
        loadingIndicator: 'p-0! w-[10px]!',
        menuList: 'max-h-[367px]!',
        loadingMessage: `${t('label:loading')}`,
        noOptionsMessage: `${t('label:noOptions')}`,
        ...props.classNameOverride
      }}
      menuHeight={375}
    />
  )
}

export default AsyncMultipleSearchWithSelectOptionGroup
